/**
 * How one job's match score adds up, part by part (2026-10-14, the "Why?" fix).
 * Pure: no Firebase, no React. Copied into functions/src/generated/ (scripts/syncFunctionsGenerated.mjs)
 * so the employer's application view and the helper describe exactly the parts the EMT sees.
 *
 * calculateJobMatch averages the parts the job states (credentials 40, location 25, experience 20,
 * skills 10) with the EMT's overall readiness (5, plus the weight of every part the job leaves out).
 * The weights always total 100, so a part's points are its score x its weight / 100. Whole points are
 * shared out by largest remainder, so the parts add up to exactly the rounded number calculateJobMatch
 * shows. When a required credential does not count, calculateJobMatch holds the total at 35: that is
 * its own line (`cap`), so parts minus the cap is still the shown score.
 *
 * Before 2026-10-14 the "Why?" panel listed only credentials, years and skills the job names. Location
 * and the overall profile could cost most of a score with nothing shown, and an empty list read
 * "You meet everything this job lists" beside 57%.
 */
import type { JobMatchScore } from '../../types/readiness';
import { US_STATES } from '../maps/usStates';
import { haversineMiles, isValidLatLng } from '../profileCoordinates';
import { DISTANCE_FREE_MILES } from './distanceScore';

export type BreakdownAudience = 'emt' | 'employer';
export type ScorePartKey = 'certifications' | 'location' | 'experience' | 'skills' | 'profile';
type LatLng = { lat: number; lng: number };

export type ScorePart = {
  key: ScorePartKey;
  /** "Credentials this job lists", "Distance", "Your overall profile". */
  label: string;
  /** Whole points this part added to the score. The parts add up to the score before any cap. */
  earned: number;
  /** This part's share of 100 for this job. */
  max: number;
  /** Plain words: what earned these points ("all met", "you live in Florida; the job is in North Carolina"). */
  text: string;
  /** Short words for "Your score is lower because of ..." ("distance (the job is in North Carolina)"). */
  reason: string;
};

export type JobScoreBreakdown = {
  /** Only the parts that went into THIS job's score, in a fixed order. */
  parts: ScorePart[];
  /** A required credential that does not count holds the score at 35: the points taken off. */
  cap: { points: number; text: string } | null;
  /** The shown score: the parts' sum minus the cap. Always equals calculateJobMatch's total. */
  total: number;
  /** Where the points of parts the job leaves out went, and the distance rule. */
  notes: string[];
  /** The job states at least one of credentials, years of experience or skills. */
  listsAnything: boolean;
  /** Every part the job states (credentials, experience, skills) is fully met. */
  meetsEverythingListed: boolean;
};

export type BreakdownContext = {
  audience?: BreakdownAudience;
  /** The EMT's saved map point (address.lat/lng), for "about N miles away" on a job scored by state. */
  emtCoords?: LatLng | null;
  /** The job's map point (getJobCoordinates). */
  jobCoords?: LatLng | null;
};

export const BREAKDOWN_LABELS: Record<BreakdownAudience, Record<ScorePartKey, string>> = {
  emt: {
    certifications: 'Credentials this job lists',
    location: 'Distance',
    experience: 'Experience this job asks for',
    skills: 'Skills this job lists',
    profile: 'Your overall profile',
  },
  employer: {
    certifications: 'Credentials this job lists',
    location: 'Distance',
    experience: 'Experience this job asks for',
    skills: 'Skills this job lists',
    profile: 'Overall profile (readiness)',
  },
};

export function stateName(code: string | null | undefined): string {
  const c = String(code ?? '').trim().toUpperCase();
  return US_STATES.find((s) => s.code === c)?.name ?? c;
}

/** The EMT's saved map point: the address's lat/lng, else geo (same rule as the Competitiveness pool). */
export function profileCoords(profile: unknown): LatLng | null {
  const p = (profile ?? {}) as { address?: { lat?: unknown; lng?: unknown }; geo?: { lat?: unknown; lng?: unknown } };
  if (isValidLatLng(p.address?.lat, p.address?.lng)) return { lat: p.address!.lat as number, lng: p.address!.lng as number };
  if (isValidLatLng(p.geo?.lat, p.geo?.lng)) return { lat: p.geo!.lat as number, lng: p.geo!.lng as number };
  return null;
}

/** "about 520 miles": nearest 10 miles (nearest 5 under 100). */
export function milesText(miles: number): string {
  const step = miles < 100 ? 5 : 10;
  const r = Math.max(step, Math.round(miles / step) * step);
  return `about ${r.toLocaleString('en-US')} miles`;
}

function yearsText(y: number): string {
  const v = Math.round(y * 10) / 10;
  return `${v} year${v === 1 ? '' : 's'}`;
}

export function joinWords(list: string[], word = 'and'): string {
  if (list.length <= 1) return list[0] ?? '';
  if (list.length === 2) return `${list[0]} ${word} ${list[1]}`;
  return `${list.slice(0, -1).join(', ')} ${word} ${list[list.length - 1]}`;
}

/**
 * Whole points for each part, largest remainder: floors first, then one point each to the parts with
 * the biggest leftover fractions until the sum is `total`.
 */
export function sharePoints(exact: number[], total: number): number[] {
  const out = exact.map((x) => Math.floor(x + 1e-9));
  let left = total - out.reduce((s, x) => s + x, 0);
  const order = exact.map((x, i) => ({ i, rem: x - Math.floor(x + 1e-9) })).sort((a, b) => b.rem - a.rem || a.i - b.i);
  for (const o of order) {
    if (left <= 0) break;
    if (o.rem > 1e-9) {
      out[o.i] += 1;
      left -= 1;
    }
  }
  // Never expected (the rounded total is at most one point per fractional part above the floors),
  // kept so the parts always add up whatever happens upstream.
  if (left !== 0 && out.length) out[out.length - 1] += left;
  return out;
}

export function jobScoreBreakdown(match: JobMatchScore, ctx: BreakdownContext = {}): JobScoreBreakdown {
  const audience = ctx.audience ?? 'emt';
  const you = audience === 'emt';
  const L = BREAKDOWN_LABELS[audience];
  const p = match.pillars;
  const parts: Array<Omit<ScorePart, 'earned'> & { exact: number; weight: number }> = [];
  const add = (key: ScorePartKey, score: number, weight: number, text: string, reason: string) =>
    parts.push({ key, label: L[key], max: weight, text, reason, exact: score * weight, weight });

  // 1. Credentials the job lists.
  if (p.certifications.applicable) {
    const c = p.certifications;
    if (c.missing.length > 0) {
      add('certifications', c.score, c.weight, `not met yet: ${joinWords(c.missing)}`, `a required credential (${joinWords(c.missing)})`);
    } else if (c.score >= 100) {
      add('certifications', c.score, c.weight, 'all met', 'the credentials it lists');
    } else {
      add(
        'certifications',
        c.score,
        c.weight,
        you
          ? 'every required one is met; the rest of this part is for the preferred ones, and you do not have all of them yet'
          : 'every required one is met; not every preferred one',
        'the preferred credentials',
      );
    }
  }

  // 2. Distance: by miles when both map points exist (distanceScore.ts, 2026-10-14), else by state.
  if (p.location.applicable && p.location.scoredBy === 'miles' && typeof p.location.miles === 'number') {
    const l = p.location;
    const emt = l.emtState ? stateName(l.emtState) : '';
    const job = l.jobState ? stateName(l.jobState) : '';
    const fromCity = typeof l.fromCity === 'string' && l.fromCity ? l.fromCity : null;
    // Measured from one of the EMT's extra cities (B4): say which, not "away" from home.
    const away = fromCity ? `${milesText(Math.max(1, l.miles as number))} from ${fromCity}` : milesText(Math.max(1, l.miles as number));
    const where = fromCity ? '' : emt && job && emt !== job ? (you ? ` (you live in ${emt}; the job is in ${job})` : ` (lives in ${emt}; the job is in ${job})`) : '';
    const theirs = you ? 'your' : 'their';
    let text: string;
    const awayWord = fromCity ? '' : ' away';
    if (l.milesReason === 'travel') text = `${you ? "you're " : ''}${away}${awayWord}, within ${theirs} travel distance (${l.travelMiles} miles)`;
    else if (l.milesReason === 'free') text = `${you ? "you're " : ''}${away}${awayWord}, within ${DISTANCE_FREE_MILES} miles`;
    else if (l.milesReason === 'relocation') text = `${you ? "you're " : ''}${away}${awayWord}${where}; ${you ? "you're open to relocating and this job accepts it" : 'open to relocating, and this job accepts it'}`;
    else text = `${you ? "you're " : ''}${away}${awayWord}${where}`;
    add('location', l.score, l.weight, text, job && emt !== job ? `distance (${away}; the job is in ${job})` : `distance (${away})`);
  } else if (p.location.applicable) {
    const l = p.location;
    const emt = l.emtState ? stateName(l.emtState) : '';
    const job = l.jobState ? stateName(l.jobState) : '';
    const miles = ctx.emtCoords && ctx.jobCoords && l.emtState !== l.jobState
      ? haversineMiles(ctx.emtCoords.lat, ctx.emtCoords.lng, ctx.jobCoords.lat, ctx.jobCoords.lng)
      : null;
    const away = miles != null && miles >= 1 ? milesText(miles) : '';
    const where = emt && job ? (you ? `you live in ${emt}; the job is in ${job}` : `lives in ${emt}; the job is in ${job}`) : '';
    const wherePlus = away ? (you ? `you're ${away} away (${where})` : `${away} away (${where})`) : where;
    let text: string;
    if (l.score >= 100) text = you ? `you live in ${emt}, the same state as this job` : `lives in ${emt}, the same state as this job`;
    else if (l.adjacent) text = `${wherePlus}, a neighboring state`;
    else if (l.score >= 70 && l.jobAcceptsRelocation && l.emtOpenToRelocation) {
      const reloc = you ? "you're open to relocating and this job accepts it" : 'open to relocating, and this job accepts it';
      text = wherePlus ? `${wherePlus}; ${reloc}` : reloc;
    } else text = wherePlus || l.explanation;
    add('location', l.score, l.weight, text, job ? `distance (the job is in ${job})` : 'distance');
  }

  // 3. Experience the job asks for.
  if (p.experience.applicable) {
    const e = p.experience;
    const text = you
      ? `you have ${yearsText(e.emtYears)}; it asks for ${yearsText(e.requiredYears)}`
      : `has ${yearsText(e.emtYears)}; the job asks for ${yearsText(e.requiredYears)}`;
    add('experience', e.score, e.weight, text, `experience (it asks for ${yearsText(e.requiredYears)})`);
  }

  // 4. Skills the job lists.
  if (p.skills.applicable) {
    const s = p.skills;
    const n = s.matched.length + s.missing.length;
    const where = you ? 'on your profile' : 'on their profile';
    const text = s.missing.length === 0
      ? `all ${n} ${where}`
      : `${s.matched.length} of ${n} ${where}; not yet: ${s.missing.slice(0, 3).join(', ')}${s.missing.length > 3 ? ` and ${s.missing.length - 3} more` : ''}`;
    add('skills', s.score, s.weight, text, `the skills it lists (${s.matched.length} of ${n})`);
  }

  // 5. The overall profile (readiness; for a driver job the driver profile, for a dispatcher job the dispatch profile).
  const r = p.readinessFloor;
  const driver = r.basis === 'driver';
  const dispatch = r.basis === 'dispatch';
  const profileText = driver
    ? `${r.score} of 100${you && r.score < 100 ? ', finish your driver documents and profile to raise this' : ''}`
    : dispatch
      ? `${r.score} of 100${you && r.score < 100 ? ', add your dispatch work and finish your profile to raise this' : ''}`
      : you
        ? `${r.score} of 100${r.score < 100 ? ', finish your profile to raise this' : ''}`
        : `readiness ${r.score} of 100`;
  parts.push({
    key: 'profile',
    label: driver ? (you ? 'Your driver profile' : 'Driver profile') : dispatch ? (you ? 'Your dispatch profile' : 'Dispatch profile') : L.profile,
    max: r.effectiveWeight,
    text: profileText,
    reason: you ? `your overall profile (${r.score} of 100)` : `the overall profile (${r.score} of 100)`,
    exact: r.score * r.effectiveWeight,
    weight: r.effectiveWeight,
  });

  // The same arithmetic as calculateJobMatch, so the parts add up to its number exactly.
  const weightSum = parts.reduce((s, x) => s + x.weight, 0);
  const rawTotal = weightSum > 0 ? Math.round(parts.reduce((s, x) => s + x.exact, 0) / weightSum) : 0;
  const earned = sharePoints(parts.map((x) => x.exact / weightSum), rawTotal);
  const finalParts: ScorePart[] = parts.map(({ exact: _e, weight: _w, ...rest }, i) => {
    void _e;
    void _w;
    return { ...rest, max: Math.round((rest.max * 100) / (weightSum || 100)), earned: earned[i] };
  });

  const total = Math.min(100, Math.max(0, match.total));
  const cap = rawTotal > total
    ? { points: rawTotal - total, text: you ? 'Held at 35 until every required credential counts' : 'Held at 35 until every required credential counts' }
    : null;

  const notes: string[] = [];
  const unstated = [
    !p.certifications.applicable ? 'credentials' : null,
    !p.experience.applicable ? 'years of experience' : null,
    !p.skills.applicable ? 'skills' : null,
  ].filter((x): x is string => Boolean(x));
  const extra = r.effectiveWeight - r.weight;
  if (extra > 0) {
    const sentences: string[] = [];
    if (unstated.length) sentences.push(`This job doesn't state ${joinWords(unstated, 'or')}.`);
    if (!p.location.applicable) {
      const why = !p.location.emtState
        ? (you ? 'your profile has no address state' : 'their profile has no address state')
        : "the job's location has no state";
      sentences.push(`Distance isn't counted, because ${why}.`);
    }
    sentences.push(`Those ${extra} points go to ${you ? 'your' : 'the'} overall profile instead.`);
    notes.push(sentences.join(' '));
  }
  if (you && p.location.applicable && p.location.score < 100) {
    notes.push(
      p.location.scoredBy === 'miles'
        ? `Distance is scored by miles in a straight line: within your travel distance or ${DISTANCE_FREE_MILES} miles earns all of its points, up to 50 miles 85%, up to 100 miles 70%, up to 200 miles 45%, and farther 20%. When you are open to relocating and the job accepts it, never below 70%.`
        : 'Distance is scored by state when the job or your address has no map location: the same state earns all of its points, a neighboring state 70%, and any other state 20%, or 70% when you are open to relocating and the job accepts it.',
    );
  }

  const listsAnything = unstated.length < 3;
  const meetsEverythingListed =
    !match.hardGateFailed &&
    (!p.certifications.applicable || p.certifications.score >= 100) &&
    (!p.experience.applicable || p.experience.score >= 100) &&
    (!p.skills.applicable || p.skills.score >= 100);

  return { parts: finalParts, cap, total, notes, listsAnything, meetsEverythingListed };
}

/** Parts that lost points, biggest loss first. */
export function partsMissingPoints(b: JobScoreBreakdown): ScorePart[] {
  return b.parts.filter((x) => x.max - x.earned >= 1).sort((a, z) => z.max - z.earned - (a.max - a.earned));
}

/**
 * The thumbs-up line for a score under 75: "Your score is lower because of distance (the job is in
 * North Carolina) and your overall profile (33 of 100)." The biggest one or two missing parts.
 */
export function lowerBecauseText(b: JobScoreBreakdown): string | null {
  const missing = partsMissingPoints(b).slice(0, 2);
  if (!missing.length) return null;
  return `Your score is lower because of ${joinWords(missing.map((m) => m.reason))}.`;
}

/** One line per part, for the helper's job summary: "Distance 5 of 25 (you live in ...)". */
export function breakdownLines(b: JobScoreBreakdown): string[] {
  const lines = b.parts.map((x) => `${x.label}: ${x.earned} of ${x.max} points (${x.text}).`);
  if (b.cap) lines.push(`${b.cap.text}: minus ${b.cap.points} points.`);
  return [...lines, ...b.notes];
}
